/**
 * Aus dem Textwust einer Erkennung die MRZ-Zeilen herausfinden.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WARUM DAS EIN EIGENER SCHRITT IST
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Eine Texterkennung liest den ganzen Ausweis: Überschriften, Namen in
 * Klarschrift, Behördennamen, Ablaufdaten, Seriennummern am Rand — und irgendwo
 * dazwischen die zwei oder drei Zeilen, um die es geht. Wer alles an
 * `leseMrz` weiterreicht, bekommt nichts.
 *
 * Dieser Schritt ist zugleich der einzige am Scanvorgang, der sich **ohne
 * Kamera** prüfen lässt: er bekommt Zeichenketten und gibt Zeichenketten
 * zurück. Genau deshalb steht er hier und nicht im Bauteil, das die Kamera
 * öffnet.
 *
 * ════════════════════════════════════════════════════════════════════════════
 *  WORAN MAN EINE MRZ-ZEILE ERKENNT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Sie besteht ausschließlich aus A–Z, 0–9 und dem Füllzeichen `<`, und sie hat
 * genau 44 (Reisepass) oder 30 Zeichen (Ausweis im Scheckkartenformat). Kein
 * Fließtext auf einem Ausweis sieht so aus — das Füllzeichen kommt sonst
 * nirgends vor.
 *
 * Die Länge allein genügt aber nicht: Erkennungen fügen gern Leerzeichen ein
 * oder hängen ein Zeichen an. Deshalb wird erst gesäubert, dann gemessen, und
 * am Ende entscheidet ohnehin die Prüfziffer in `leseMrz`.
 */

/** Nur A–Z, 0–9 und `<` bleiben stehen. */
function saeubern(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9<]/g, '')
}

/**
 * Kandidaten für MRZ-Zeilen, in der Reihenfolge, in der sie erkannt wurden.
 *
 * @param bloecke Was die Erkennung geliefert hat — je Eintrag eine Zeile oder
 *   ein Block. Mehrzeilige Blöcke werden aufgetrennt.
 */
export function mrzZeilen(bloecke: string[]): string[] {
  const zeilen = bloecke
    .flatMap((b) => b.split(/[\r\n]+/))
    .map(saeubern)
    .filter((z) => z.length === 44 || z.length === 30)

  /*
   * Nur Zeilen gleicher Länge gehören zusammen. Ein Ausweis liefert nie beides
   * — und stünde zufällig eine 30er neben zwei 44ern, wäre sie kein Teil
   * derselben MRZ.
   */
  const vierziger = zeilen.filter((z) => z.length === 44)
  if (vierziger.length >= 2) return vierziger.slice(0, 2)

  const dreissiger = zeilen.filter((z) => z.length === 30)
  if (dreissiger.length >= 3) return dreissiger.slice(0, 3)

  return []
}

/**
 * Ein zweiter Anlauf für den häufigsten Erkennungsfehler: die MRZ wird als
 * EIN Block ohne Zeilenumbrüche geliefert.
 *
 * Dann steht dort eine Kette aus 88 oder 90 Zeichen, die sich sauber teilen
 * lässt. Das passiert oft genug, um es abzufangen — und es kostet nichts, weil
 * die Prüfziffern hinterher ohnehin entscheiden.
 */
export function mrzAusEinerKette(text: string): string[] {
  const s = saeubern(text)
  if (s.length === 88) return [s.slice(0, 44), s.slice(44)]
  if (s.length === 90) return [s.slice(0, 30), s.slice(30, 60), s.slice(60)]
  return []
}

/**
 * Beide Wege nacheinander: erst die getrennten Zeilen, dann die Kette.
 *
 * Gibt eine leere Liste zurück, wenn nichts wie eine MRZ aussieht — der
 * Aufrufer zeigt dann „nicht erkannt, bitte noch einmal" und nicht einen
 * Fehler. Ein Ausweis, der schief liegt, ist kein Programmfehler.
 */
export function findeMrz(bloecke: string[]): string[] {
  const getrennt = mrzZeilen(bloecke)
  if (getrennt.length > 0) return getrennt

  for (const b of bloecke) {
    const ausKette = mrzAusEinerKette(b)
    if (ausKette.length > 0) return ausKette
  }
  // Letzter Versuch: alles zusammengeschoben. Manche Erkennungen liefern die
  // zwei Zeilen als zwei Blöcke ohne erkennbaren Umbruch dazwischen.
  return mrzAusEinerKette(bloecke.join(''))
}
